<script lang="ts">
    import { AppStore, Panels } from "$/stores/AppStore";
    import Panel from "../_components/Panel.svelte";
    import PanelHeader from "../_components/PanelHeader.svelte";
    import { TokenStore } from "$/stores/TokenStore.js";
    import { UserService } from "$/services/UserService";
    import { ActiveTopicStore, ActiveTopic } from "$/stores/extension/ActiveTopicStore";
    import { RecentTopicsStore } from "$/stores/RecentTopicsStore";
    import { TenantStore } from "$/stores/TenantStore";
    import { ClusterStore } from "$/stores/ClusterStore";
    import PanelDeleteSection from "$/components/panels/_components/PanelDeleteSection.svelte";
    import { redirect } from "@roxi/routify";
    import { extensionLogout } from "$/utils/Utils";

    let resetPasswordEmailSent: boolean = false;
    let loading: boolean = false;

    async function handleUserDataChanged(event: Event, field: string) {
        let fieldValue = (event.target as HTMLInputElement).value;
        if (
            fieldValue.trim() !== "" &&
            $AppStore.panel === Panels.USER_SETTINGS
        ) {
            const user = { ...$TokenStore?.user, [field]: fieldValue };
            await UserService.updateUser(user);
            await TokenStore.updateTokenStoreUser(user);
            $RecentTopicsStore && RecentTopicsStore.updateTopicsUser(user);
            $ActiveTopic && ActiveTopicStore.updateTopicUser(user);
            $TenantStore && TenantStore.updateAuthoritiesUser(user);
            $ClusterStore && ClusterStore.updateClusterRolesUser(user);
        } else {
            (event.target as HTMLInputElement).value = $TokenStore?.user[field];
        }
    }

    async function sendResetPassword() {
        if (!loading) {
            loading = true;
            await UserService.resetPassword();
            resetPasswordEmailSent = true;
        }
    }

    async function deleteUserAccount(password: string) {
        await UserService.deleteAccount(password);
        extensionLogout();
        $redirect("/auth/logout");
    }
</script>

<Panel>
    <PanelHeader title="User Settings" on:close={AppStore.closePanels} />

    <div class="panel-content">
        <div class="user-info">
            <h2 class="user-info-header">User info</h2>
            <div class="user-info-data">
                <div class="user-data">
                    <div>
                        {$TokenStore.user?.firstName}
                        {$TokenStore.user?.lastName}
                    </div>
                    <div class="user-email">
                        {$TokenStore.user?.email}
                    </div>
                </div>
                <div class="user-info-description">Current info</div>
            </div>
        </div>

        <div class="form-group">
            <h2 class="input-label">First and Last name</h2>
            <div class="multi-input-wrapper">
                <div class="input-group">
                    <div class="input-wrapper">
                        <input
                            class="form-input"
                            placeholder="First Name"
                            value={$TokenStore?.user?.firstName}
                            on:change={(e) =>
                                handleUserDataChanged(e, "firstName")}
                        />
                    </div>
                </div>
                <div class="input-group">
                    <div class="input-wrapper">
                        <input
                            class="form-input"
                            placeholder="Last Name"
                            value={$TokenStore?.user?.lastName}
                            on:change={(e) =>
                                handleUserDataChanged(e, "lastName")}
                        />
                    </div>
                </div>
            </div>
        </div>

        <div class="form-group">
            <h2 class="input-label">Password</h2>
            <div class="change-password-wrapper">
                <div class="change-password-placeholder">
                    <svg
                        class="change-password-placeholder__icon"
                        viewBox="0 0 24 25"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M16.25 5.01953C17.9069 5.01953 19.25 6.36268 19.25 8.01953M22.25 8.01953C22.25 11.3332 19.5637 14.0195 16.25 14.0195C15.8993 14.0195 15.5555 13.9894 15.2213 13.9317C14.6583 13.8344 14.062 13.9575 13.658 14.3615L11 17.0195H8.75V19.2695H6.5V21.5195H2.75V18.7015C2.75 18.1048 2.98705 17.5325 3.40901 17.1105L9.908 10.6115C10.312 10.2075 10.4351 9.61126 10.3378 9.04823C10.2801 8.71399 10.25 8.37028 10.25 8.01953C10.25 4.70582 12.9363 2.01953 16.25 2.01953C19.5637 2.01953 22.25 4.70582 22.25 8.01953Z"
                            stroke="#0F172A"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                    <span class="change-password-placeholder__text"
                        >Reset your password</span
                    >
                    <button
                        on:click={sendResetPassword}
                        class={"change-password-placeholder__button " +
                            (loading
                                ? "bg-gray-100 text-black"
                                : "bg-pink-600 hover:bg-rose-700")}
                        disabled={loading}
                    >
                        Reset
                    </button>
                </div>
                <p
                    class="reset-password-message-sent"
                    class:hidden={!resetPasswordEmailSent}
                >
                    Check your <b>email inbox</b> to reset your password
                </p>
            </div>
        </div>

        <PanelDeleteSection
            deletionConfirmed={deleteUserAccount}
            description={"Deleting you account will terminate all of your info and subscriptions."}
            warningText={"Are you <b>absolutely sure</b> you want to delete your account?"}
            deleteBtnText={"Delete your account"}
            confirmDeletionWithEmail={true}
        />
    </div>
</Panel>

<style>
    @import "../../../main.css";

    .panel-content {
        @apply reset flex flex-col gap-8 overflow-auto px-4 py-4 !important;
    }

    .input-group {
        @apply reset flex flex-shrink-0 flex-col gap-2 !important;
    }

    .input-label {
        @apply reset !important;
    }

    .user-info {
        @apply flex flex-col !important;
    }

    .user-info-header {
        @apply reset mb-2 !important;
    }

    .user-info-data {
        @apply relative flex flex-row items-center overflow-clip rounded-2xl border border-gray-200 bg-white px-4 py-2 !important;
    }

    .user-data {
        @apply flex flex-col text-sm !important;
    }

    .user-email {
        @apply text-xs !important;
    }

    .user-info-description {
        @apply ml-auto text-xs italic text-gray-400 !important;
    }

    .form-group {
        @apply flex flex-col !important;
    }

    .input-label {
        @apply reset mb-2 !important;
    }

    .form-input {
        @apply w-full text-sm outline-none !important;
    }

    .input-wrapper {
        @apply flex flex-row items-center gap-1 rounded-full border border-gray-200 bg-white p-4 !important;
    }

    .multi-input-wrapper {
        @apply flex flex-col gap-2;
    }

    .change-password-wrapper {
        @apply reset flex w-full flex-col items-stretch !important;
    }

    .change-password-placeholder {
        @apply reset flex h-12 flex-row items-center gap-2 rounded-full border border-solid border-gray-200 bg-white py-2 pl-4 pr-2 !important;
    }

    .change-password-placeholder__text {
        @apply reset text-sm text-black !important;
    }

    .change-password-placeholder__button {
        @apply ml-auto h-8 rounded-full px-4 py-1 text-xs tracking-tight text-white transition-all hover:scale-105;
    }

    .add-users-placeholder__button__bold {
        @apply reset text-xs font-bold tracking-tight text-inherit !important;
    }

    .change-password-placeholder__icon {
        @apply reset h-5 w-5 text-black !important;
    }

    .change-password-placeholder-message {
        @apply reset mt-1 text-center text-xs text-gray-300 hover:text-black !important;
    }

    .change-password-placeholder-message__bold {
        @apply reset text-xs font-bold text-inherit !important;
    }

    .reset-password-message-sent {
        @apply mt-1 text-left text-xs text-gray-300 hover:text-black;
    }
</style>
